import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Play, CheckCircle2, SkipForward, RotateCcw } from 'lucide-react';

interface Exercise {
  primary: string;
  alternatives: string[];
  sets: number;
  target: string;
}

interface StructuredWorkoutPlanProps {
  templateType: 'fullBody' | 'upperLower';
  workoutType?: 'upperA' | 'upperB' | 'lowerA' | 'lowerB' | 'fullBodyA' | 'fullBodyB'; // A/B variations
  exercises: Exercise[];
  onStartWorkout: (selectedExercises: Array<{ exercise: string; sets: number; isAlternative?: boolean }>) => void;
  onBack: () => void;
}

interface ExerciseSelection {
  index: number;
  selected: string | null; // null means skipped
  isAlternative: boolean;
}

export default function StructuredWorkoutPlan({ 
  templateType: _templateType, 
  workoutType,
  exercises, 
  onStartWorkout, 
  onBack 
}: StructuredWorkoutPlanProps) {
  const [selections, setSelections] = useState<ExerciseSelection[]>(
    exercises.map((exercise, index) => ({
      index,
      selected: exercise.primary, // Pre-select primary exercise
      isAlternative: false
    }))
  );

  const handleExerciseSelect = (exerciseIndex: number, exerciseName: string, isAlternative: boolean = false) => {
    setSelections(prev => prev.map(selection => 
      selection.index === exerciseIndex
        ? { ...selection, selected: exerciseName, isAlternative }
        : selection
    ));
  };

  const handleSkip = (exerciseIndex: number) => {
    setSelections(prev => prev.map(selection =>
      selection.index === exerciseIndex
        ? { ...selection, selected: null, isAlternative: false }
        : selection
    ));
  };

  const handleStartWorkout = () => {
    const selectedExercises = selections
      .filter(sel => sel.selected !== null)
      .map(sel => ({
        exercise: sel.selected!,
        sets: exercises[sel.index].sets,
        isAlternative: sel.isAlternative
      }));
    
    onStartWorkout(selectedExercises);
  };

  const getWorkoutTitle = () => {
    if (workoutType?.startsWith('fullBody')) return 'Full Body Workout';
    if (workoutType?.startsWith('upper')) return 'Upper Body Workout';
    if (workoutType?.startsWith('lower')) return 'Lower Body Workout';
    return 'Workout Plan';
  };

  const selectedCount = selections.filter(sel => sel.selected !== null).length;
  const totalExercises = exercises.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Badge variant="secondary" className="text-xs">
          {selectedCount}/{totalExercises} exercises
        </Badge>
      </div>

      <div className="text-center">
        <h2 className="text-2xl font-bold tracking-tight mb-2">{getWorkoutTitle()}</h2>
        <p className="text-muted-foreground">
          Choose your exercises for today's session. Select primary exercises or alternatives.
        </p>
      </div>

      {/* Exercise List */}
      <div className="space-y-4">
        {exercises.map((exercise, index) => {
          const selection = selections[index];
          const isSelected = selection.selected !== null;
          const isSkipped = selection.selected === null;
          
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className={`transition-all ${
                isSelected ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20' : 
                isSkipped ? 'border-orange-300 bg-orange-50/50 dark:bg-orange-950/20' : 
                'border-border hover:border-primary/50'
              }`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="w-6 h-6 bg-muted rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        {exercise.primary}
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        )}
                      </CardTitle>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {exercise.sets} sets
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {exercise.target}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <AnimatePresence>
                    {isSelected && selection.selected && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-green-100/50 dark:bg-green-900/20 border border-green-200/50 dark:border-green-800/30 rounded-lg p-3 mb-4"
                      >
                        <div className="text-sm font-medium text-green-800 dark:text-green-200">
                          Selected: {selection.selected}
                          {selection.isAlternative && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Alternative
                            </Badge>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {/* Primary Exercise */}
                    <Button
                      variant={selection.selected === exercise.primary ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleExerciseSelect(index, exercise.primary, false)}
                      className="h-8"
                    >
                      {exercise.primary}
                    </Button>

                    {/* Alternative Exercises */}
                    {exercise.alternatives.map((alternative, altIndex) => (
                      <Button
                        key={altIndex}
                        variant={selection.selected === alternative ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleExerciseSelect(index, alternative, true)}
                        className="h-8"
                      >
                        {alternative}
                      </Button>
                    ))}

                    {/* Skip Button */}
                    <Button
                      variant={isSkipped && selection.selected === null ? "destructive" : "ghost"}
                      size="sm"
                      onClick={() => handleSkip(index)}
                      className="h-8 ml-auto"
                    >
                      <SkipForward className="w-3 h-3 mr-1" />
                      Skip
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Start Workout Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: exercises.length * 0.1 + 0.2 }}
      >
        <Button
          onClick={handleStartWorkout}
          disabled={selectedCount === 0}
          className="w-full h-12 text-base font-medium"
          size="lg"
        >
          <Play className="w-4 h-4 mr-2" />
          Start Workout ({selectedCount} exercises)
        </Button>
        
        {selectedCount === 0 && (
          <p className="text-center text-sm text-muted-foreground mt-2">
            Select at least one exercise to start your workout
          </p>
        )}
      </motion.div>

      {/* Reset Button */}
      <Button
        variant="outline"
        onClick={() => setSelections(exercises.map((_, index) => ({
          index,
          selected: null,
          isAlternative: false
        })))}
        className="w-full"
        disabled={selectedCount === 0}
      >
        <RotateCcw className="w-4 h-4 mr-2" />
        Reset Selections
      </Button>
    </div>
  );
}